/**
 * Shortcut Help Component
 * 
 * Modern interface for displaying all available keyboard shortcuts
 * according to the current application context with:
 * - Contextual shortcut display
 * - Categorization by action type
 * - Modern and responsive interface
 * - Search functionality
 * - Keyboard shortcut categories
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @returns {JSX.Element} The shortcut help component
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Keyboard as KeyboardIcon,
  Navigation as NavigationIcon,
  Settings as SettingsIcon,
  Help as HelpIcon
} from '@mui/icons-material';

// Shortcut categories for organization
const SHORTCUT_CATEGORIES = {
  navigation: {
    title: 'Navigation',
    icon: NavigationIcon,
    color: '#2196f3',
    description: 'Shortcuts for quick application navigation'
  },
  actions: {
    title: 'Actions',
    icon: SettingsIcon,
    color: '#4caf50',
    description: 'Shortcuts for common actions'
  },
  system: {
    title: 'System',
    icon: KeyboardIcon,
    color: '#ff9800',
    description: 'System and interface shortcuts'
  },
  contextual: {
    title: 'Contextual',
    icon: HelpIcon,
    color: '#9c27b0',
    description: 'Shortcuts specific to the current page'
  }
};
// Function to categorize shortcuts
const categorizeShortcuts = (shortcuts) => {
  const categories = {
    navigation: [],
    actions: [],
    system: [],
    contextual: []
  };

  shortcuts.forEach(shortcut => {
    if (shortcut.key.startsWith('g+')) {
      categories.navigation.push(shortcut);
    } else if (shortcut.key.includes('ctrl+shift') || shortcut.key === 'escape' || shortcut.key === '?') {
      categories.system.push(shortcut);
    } else if (shortcut.key.includes('ctrl+')) {
      categories.actions.push(shortcut);
    } else {
      categories.contextual.push(shortcut);
    }
  });

  return categories;
};

// Shortcut key display component
const KeyboardKey = ({ keyText, variant = 'default' }) => {
  const getKeyStyle = () => {
    switch (variant) {
      case 'modify':
        return {
          backgroundColor: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '2px 6px',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          color: '#666',
          minWidth: '24px',
          textAlign: 'center',
          display: 'inline-block'
        };
      case 'sequence':
        return {
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '4px',
          padding: '2px 6px',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          color: '#1976d2',
          minWidth: '24px',
          textAlign: 'center',
          display: 'inline-block'
        };
      default:
        return {
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '2px 6px',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          color: '#333',
          minWidth: '24px',
          textAlign: 'center',
          display: 'inline-block',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        };
    }
  };

  return (
    <span style={getKeyStyle()}>
      {keyText}
    </span>
  );
};

// Formatted shortcut display component
const ShortcutDisplay = ({ shortcut }) => {
  const formatShortcutKey = (key) => {
    const parts = key.split('+');
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {index > 0 && <span style={{ margin: '0 4px', color: '#666' }}>+</span>}
        <KeyboardKey 
          keyText={part.charAt(0).toUpperCase() + part.slice(1)} 
          variant={['ctrl', 'cmd', 'shift', 'alt'].includes(part.toLowerCase()) ? 'modify' : 
                   part.length === 1 ? 'sequence' : 'default'}
        />
      </React.Fragment>
    ));
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      py: 1,
      px: 2,
      '&:hover': {
        backgroundColor: 'action.hover',
        borderRadius: 1
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        {formatShortcutKey(shortcut.key)}
      </Box>
      <Typography variant="body2" sx={{ ml: 2, flex: 2, color: 'text.secondary' }}>
        {shortcut.description}
      </Typography>
    </Box>
  );
};

const ShortcutHelp = ({ open, onClose, shortcutHelp }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter shortcuts by search term
  const filteredShortcuts = useMemo(() => {
    if (!shortcutHelp?.shortcuts) return [];
    
    return shortcutHelp.shortcuts.filter(shortcut =>
      shortcut.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shortcut.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [shortcutHelp?.shortcuts, searchTerm]);

  // Categorize filtered shortcuts
  const categorizedShortcuts = useMemo(() => {
    return categorizeShortcuts(filteredShortcuts);
  }, [filteredShortcuts]);

  if (!shortcutHelp) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <KeyboardIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            {t('shortcuts.keyboardShortcuts', 'Keyboard Shortcuts')}
          </Typography>
          <Chip 
            label={shortcutHelp.context} 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ ml: 2 }}
          />
        </Box>
        <Tooltip title="Close (Escape)">
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Search bar */}
        <TextField
          fullWidth
          label={t('shortcuts.searchShortcuts', 'Search shortcuts')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 3 }}
        />

        {/* Statistics */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{filteredShortcuts.length}</strong> {t('shortcuts.availableInContext', 'shortcuts available in context')} <strong>{shortcutHelp.context}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Press <KeyboardKey keyText="?" variant="sequence" /> to show/hide this help
          </Typography>
        </Box>

        {/* Shortcuts by category */}
        <Grid container spacing={2}>
          {Object.entries(SHORTCUT_CATEGORIES).map(([categoryKey, category]) => {
            const shortcuts = categorizedShortcuts[categoryKey];
            if (shortcuts.length === 0) return null;

            const IconComponent = category.icon;

            return (
              <Grid item xs={12} md={6} key={categoryKey}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <IconComponent 
                        sx={{ 
                          mr: 1, 
                          color: category.color,
                          fontSize: '1.2rem'
                        }} 
                      />
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                        {category.title}
                      </Typography>
                      <Chip 
                        label={shortcuts.length} 
                        size="small" 
                        sx={{ ml: 'auto', minWidth: '24px' }}
                      />
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      {category.description}
                    </Typography>

                    <Divider sx={{ mb: 1 }} />

                    <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                      {shortcuts.map((shortcut, index) => (
                        <ShortcutDisplay key={index} shortcut={shortcut} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Message if no results */}
        {filteredShortcuts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No shortcut found for "{searchTerm}"
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('shortcuts.tryAnotherSearch', 'Try a different search term')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {t('shortcuts.autoAdapt', 'Shortcuts automatically adapt to the current page context')}
        </Typography>
        <Button onClick={onClose} variant="outlined">{t('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ShortcutHelp);

